import {Injectable, Injector, Type} from '@angular/core';
import {EMPTY, Observable, of, ReplaySubject, Subject, take} from 'rxjs';
import {environment} from 'src/environments/environment';
import {PlatformDescriptor} from '../utils/platform-descriptor';
import {DesktopNativeApi} from '../books/services/api/impl/desktop-books-native-api';
import {EmulatedNativeServiceApi} from '../books/services/api/impl/emulated-books-native-api';
import {IosNativeApi} from '../books/services/api/impl/ios-books-native-api';
import {WebNativeServiceApi} from '../books/services/api/impl/web-native-api.service';
import {UnsupportedVideoApi, VideoApi, VideoRestService} from "../lessons/services/video-rest.service";
import {EmptyRecorder, Recorder} from "../cspa/model/recorder";
import {CasaNativeApiV3} from "../auth_profile/services/api/casa-native-api";
import {UnsupportedCasaNativeApi} from "../auth_profile/services/api/impl/rest/unsupported-casa-native-api.service";
import {BooksNativeServiceApi} from "../books/services/api/books-native-api";
import {BooksNativeServiceApiV3} from "../books/services/api/books-native-api-v3";
import {WebNativeServiceApiV3} from "../books/services/api/impl/web-native-api-v3.service";
import {LegacyBooksBridgeV3} from "../books/services/api/impl/legacy-books-bridge-v3.service";
import {AndroidBooksNativeApi} from "../books/services/api/impl/android-books-native-api";
import {IosNewNativeApi} from "../cspa/services/api/impl/ios-new-native-api";
import {AndroidRequestBasedNativeApi} from "../cspa/services/api/impl/AndroidRequestBasedNativeApi";
import {MobileNativeAudioRecorderAdapter} from "../cspa/services/api/mobile-native-audio-recorder-adapter";
import {CspaRestV3} from "../cspa/services/v3/cspa-rest-v3";
import {CspaRestV3Service} from "../cspa/services/v3/cspa-rest-v3.service";
import {CspaRestV3IosService} from "../cspa/services/v3/cspa-rest-v3-ios.service";
import {CspaRestV3AndroidService} from "../cspa/services/v3/cspa-rest-v3-android.service";
import {PropertiesNativeServiceApi} from './api/properties-native-api';
import {WebPropertiesNativeApi} from './api/impl/web-properties-native-api';
import {IosPropertiesNativeApi} from './api/impl/ios-properties-native-api';
import {AndroidPropertiesNativeApi} from './api/impl/android-properties-native-api';
import {EmulatedPropertiesNativeApi} from './api/impl/emulated-properties-native-api';
import {DesktopPropertiesNativeApi} from './api/impl/desktop-properties-native-api';
import {WebColNativeServiceApi} from '../col/services/api/impl/web-col-native-api.service';
import {UnsupportedColNativeServiceApi} from '../col/services/api/impl/unsupported-col-native-api.service';
import {ColNativeServiceApi} from '../col/services/api/col-native-api';
import {CasaEmulatedService} from "../auth_profile/services/api/impl/casa-emulated.service";
import {CasaAndroidService} from "../auth_profile/services/api/impl/casa-android.service";
import {CasaIosService} from "../auth_profile/services/api/impl/casa-ios.service";
import {VideoAndroidService} from "../lessons/services/api/impl/rest/video-android.service";
import {VideoIosService} from "../lessons/services/api/impl/rest/video-ios.service";
import {ColAndroidService} from "../col/services/api/impl/col-android.service";
import {ColIosService} from "../col/services/api/impl/col-ios.service";
import {WebCasaNativeV3Service} from "../auth_profile/services/api/impl/rest/v3/web-casa-native-v3.service";
import {ColNativeV3Api} from "../col/services/api/v3/col-native-v3-api";
import {WebColNativeV3ServiceApi} from "../col/services/api/v3/web-col-native-v3-api.service";
import {ColAndroidV3Service} from "../col/services/api/v3/col-android-v3.service";
import {ColIosV3Service} from "../col/services/api/v3/col-ios-v3.service";
import {UnsupportedColNativeV3ServiceApi} from "../col/services/api/v3/unsupported-col-native-v3-api.service";

@Injectable({
  providedIn: 'root'
})
export class NativeServiceApiProvider {
  caches: {[cacheName: string]: Subject<any>} = {}

  constructor(
    private injector: Injector) {
  }

  cacheChain<T>(name: string, instanceBuilder: () => Observable<T>): Observable<T> {
    let instanceSubject: Subject<T> = this.caches[name];
    if (instanceSubject) return instanceSubject.asObservable().pipe(take(1))
    instanceSubject = new ReplaySubject<T>(1)
    this.caches[name] = instanceSubject
    instanceBuilder().subscribe(result =>
      instanceSubject.next(result)
    )
    return instanceSubject.asObservable().pipe(take(1))
  }

  books(): Observable<BooksNativeServiceApi> {
    return this.cacheChain<BooksNativeServiceApi>("books", () =>
      of(this.injector.get(this.matchApiClass(environment.platform)))
    );
  }

  booksV3(): Observable<BooksNativeServiceApiV3> {
    return this.cacheChain<BooksNativeServiceApiV3>("booksV3", () =>
      of(this.injector.get(this.matchBooksV3ApiClass(environment.platform)))
    );
  }

  casa(): Observable<CasaNativeApiV3> {
    return this.cacheChain<CasaNativeApiV3>("casa", () =>
      of(this.injector.get(this.matchCasaApiClass(environment.platform)))
    )
  }

  video(): Observable<VideoApi> {
    return this.cacheChain<VideoApi>("video", () =>
      of(this.injector.get(this.matchVideoApiClass(environment.platform)))
    )
  }

  properties(): Observable<PropertiesNativeServiceApi> {
    return this.cacheChain<PropertiesNativeServiceApi>("properties", () =>
      of(this.injector.get(this.matchPropertiesApiClass(environment.platform)))
    )
  }

  col(): Observable<ColNativeServiceApi> {
    return this.cacheChain<ColNativeServiceApi>("col", () =>
      of(this.injector.get(this.matchColApiClass(environment.platform)))
    )
  }

  colV3(): Observable<ColNativeV3Api> {
    return this.cacheChain<ColNativeV3Api>("colV3", () =>
      of(this.injector.get(this.matchColApiClassV3(environment.platform)))
    )
  }

  private cspaRestV3(): Observable<CspaRestV3> {
    return this.cacheChain<CspaRestV3>("cspaRestV3", () =>
      of(this.injector.get(CspaRestV3Service))
    );
  }

  private cspaRestV3Ios(): Observable<CspaRestV3> {
    return this.cacheChain<CspaRestV3>("cspaRestV3Ios", () =>
      of(this.injector.get(CspaRestV3IosService))
    );
  }

  private cspaRestV3Android(): Observable<CspaRestV3> {
    return this.cacheChain<CspaRestV3>("cspaRestV3Android", () =>
      of(this.injector.get(CspaRestV3AndroidService))
    );
  }

  private iosCspaNative(): Observable<IosNewNativeApi> {
    return this.cacheChain("iosCspaNative", () =>
      of(this.injector.get(IosNewNativeApi))
    )
  }

  private androidCspaNative(): Observable<AndroidRequestBasedNativeApi> {
    return this.cacheChain("androidCspaNative", () =>
      of(this.injector.get(AndroidRequestBasedNativeApi))
    )
  }

  cspaV3(): Observable<CspaRestV3> {
    return this.cacheChain<CspaRestV3>("cspaV3", () => {
      const platform = environment.platform;
      if (platform.name === PlatformDescriptor.web.name) {
        return this.cspaRestV3();
      }
      if (platform.name === PlatformDescriptor.emulated.name) {
        return this.cspaRestV3();
      }
      if (environment.ssmiEnabled && platform.name === PlatformDescriptor.iOS.name) {
        return this.cspaRestV3Ios();
      }
      if (environment.ssmiEnabled && platform.name === PlatformDescriptor.Android.name) {
        return this.cspaRestV3Android();
      }
      return this.cspaRestV3();
    });
  }

  private iosNativeAudio(): Observable<Recorder> {
    return this.cacheChain("iosNativeAudio", () => {
      return new Observable<Recorder>( observer => {
          this.iosCspaNative().subscribe( iosNative => {
            iosNative.initAudio( state => {
              if (state) {
                observer.next(new MobileNativeAudioRecorderAdapter(iosNative));
                observer.complete();
              } else {
                observer.next(new EmptyRecorder());
                observer.complete();
              }
            })
          })
        }
      )
    })
  }

  private androidNativeAudio(): Observable<Recorder> {
    return this.cacheChain("androidNativeAudio", () => {
      return new Observable<Recorder>( observer => {
          this.androidCspaNative().subscribe( androidNative => {
            androidNative.initAudio( state => {
              if (state) {
                observer.next(new MobileNativeAudioRecorderAdapter(androidNative));
                observer.complete();
              } else {
                observer.next(new EmptyRecorder());
                observer.complete();
              }
            })
          })
        }
      )
    })
  }

  public getAlternativeAudioApi(): Observable<Recorder> {
    const platform = environment.platform;
    if (platform.name === PlatformDescriptor.web.name
      || platform.name === PlatformDescriptor.emulated.name
    ) {
      return EMPTY;
    }
    if (platform.name === PlatformDescriptor.Android.name) {
      return this.androidNativeAudio();
    }
    if (platform.name === PlatformDescriptor.iOS.name) {
      return this.iosNativeAudio();
    }
    throw new Error("not supported platform for video api");

  }

  private matchVideoApiClass(platform: PlatformDescriptor): Type<any> {
    if (platform.name === PlatformDescriptor.web.name) {
      return VideoRestService;
    } else if (platform.name === PlatformDescriptor.emulated.name) {
      return VideoRestService;
    } else if (environment.ssmiEnabled && platform.name === PlatformDescriptor.Android.name) {
      return VideoAndroidService;
    } else if (environment.ssmiEnabled && platform.name === PlatformDescriptor.iOS.name) {
      return VideoIosService;
    }
    return UnsupportedVideoApi;
  }

  private matchCasaApiClass(platform: PlatformDescriptor): Type<any> {
    if (platform.name === PlatformDescriptor.web.name) {
      return WebCasaNativeV3Service;
    } else if (platform.name === PlatformDescriptor.emulated.name) {
      return CasaEmulatedService;
    } else if (environment.ssmiEnabled && platform.name === PlatformDescriptor.Android.name) {
      return CasaAndroidService
    } else if (environment.ssmiEnabled && platform.name === PlatformDescriptor.iOS.name) {
      return CasaIosService
    }
    return UnsupportedCasaNativeApi;
  }

  private matchApiClass(platform: PlatformDescriptor): Type<any> {
    if (platform.name === PlatformDescriptor.iOS.name) {
      return IosNativeApi;
    } else if (platform.name === PlatformDescriptor.Android.name) {
      return AndroidBooksNativeApi;
    } else if (platform.name === PlatformDescriptor.web.name) {
      return WebNativeServiceApi;
    } else if (platform.name === PlatformDescriptor.emulated.name) {
      return EmulatedNativeServiceApi;
    } else if (platform.name === PlatformDescriptor.Desktop.name) {
      return DesktopNativeApi;
    }
    return null
  }

  private matchBooksV3ApiClass(platform: PlatformDescriptor): Type<any> {
    if (platform.name === PlatformDescriptor.web.name) {
      return WebNativeServiceApiV3;
    }
    // other platforms keep the books data on the legacy native bridge,
    // exposed through the legacy->v3 mapping bridge
    return LegacyBooksBridgeV3;
  }

  private matchPropertiesApiClass(platform: PlatformDescriptor): Type<any> {
    if (platform.name === PlatformDescriptor.iOS.name) {
      return IosPropertiesNativeApi;
    } else if (platform.name === PlatformDescriptor.Android.name) {
      return AndroidPropertiesNativeApi;
    } else if (platform.name === PlatformDescriptor.web.name) {
      return WebPropertiesNativeApi;
    } else if (platform.name === PlatformDescriptor.emulated.name) {
      return EmulatedPropertiesNativeApi;
    } else if (platform.name === PlatformDescriptor.Desktop.name) {
      return DesktopPropertiesNativeApi;
    }
    return null
  }

  private matchColApiClass(platform: PlatformDescriptor): Type<any> {
    if (platform.name === PlatformDescriptor.web.name) {
      return WebColNativeServiceApi;
    } else if (platform.name === PlatformDescriptor.emulated.name) {
      return WebColNativeServiceApi;
    } else if (platform.name === PlatformDescriptor.Android.name) {
      return ColAndroidService;
    } else if (platform.name === PlatformDescriptor.iOS.name) {
      return ColIosService;
    }
    return UnsupportedColNativeServiceApi;
  }

  private matchColApiClassV3(platform: PlatformDescriptor): Type<any> {
    if (platform.name === PlatformDescriptor.web.name) {
      return WebColNativeV3ServiceApi;
    } else if (platform.name === PlatformDescriptor.emulated.name) {
      return WebColNativeV3ServiceApi;
    } else if (platform.name === PlatformDescriptor.Android.name) {
      return ColAndroidV3Service;
    } else if (platform.name === PlatformDescriptor.iOS.name) {
      return ColIosV3Service;
    }
    return UnsupportedColNativeV3ServiceApi;
  }
}
